'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      router.push('/student-dashboard');
    } catch (err: any) {
      setError(err?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--primary)' }}>
      {/* Left panel — decorative */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center relative overflow-hidden px-16"
        style={{ background: 'linear-gradient(160deg, #1a2b22 0%, #22382c 60%, #141e18 100%)' }}>
        {/* Gold top line */}
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)' }} />
        {/* Vertical accent */}
        <div className="absolute right-0 top-0 bottom-0 w-px" style={{ background: 'linear-gradient(180deg, transparent, rgba(201,168,76,0.2), transparent)' }} />

        <div className="flex flex-col items-center text-center max-w-sm">
          <div className="relative mb-10" style={{ width: 220, height: 220 }}>
            <Image
              src="/assets/images/REUSA_PNG_LOGO__4_-1781810452969.png"
              alt="REUSA Official Crest"
              fill
              className="object-contain drop-shadow-2xl"
              priority
              sizes="220px"
            />
          </div>
          <div className="w-12 h-px mb-8" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)' }} />
          <p className="font-serif italic text-white/40 text-sm leading-relaxed tracking-wide">
            "Education is the most powerful weapon which you can use to change the world."
          </p>
          <p className="font-sans text-white/25 text-xs mt-4 tracking-widest uppercase">Nelson Mandela</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 bg-white">
        {/* Top-left logo — links back to homepage */}
        <div className="absolute top-0 right-0 left-0 lg:left-1/2 flex items-center px-6 h-16 pointer-events-none">
          <Link href="/" className="pointer-events-auto flex items-center gap-2 hover:opacity-80 transition-opacity" aria-label="REUSA Home">
            <Image
              src="/assets/images/REUSA_PNG_LOGO__4_-1781810452969.png"
              alt="REUSA logo"
              width={88}
              height={32}
              className="object-contain"
              priority
            />
          </Link>
        </div>

        {/* Mobile logo */}
        <div className="lg:hidden mb-10 relative" style={{ width: 96, height: 96 }}>
          <Image
            src="/assets/images/REUSA_PNG_LOGO__4_-1781810452969.png"
            alt="REUSA Crest"
            fill
            className="object-contain"
            sizes="96px"
          />
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-10">
            <h1 className="font-serif font-bold text-3xl text-primary mb-2 leading-tight">
              Student Portal
            </h1>
            <p className="font-sans text-sm text-muted-foreground">
              Sign in to access your learning dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block font-sans text-xs font-semibold text-foreground/70 tracking-widest uppercase mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="w-full px-4 py-3.5 bg-secondary border border-border rounded-xl font-sans text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <div>
              <label className="block font-sans text-xs font-semibold text-foreground/70 tracking-widest uppercase mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full px-4 py-3.5 bg-secondary border border-border rounded-xl font-sans text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="font-sans text-xs text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl font-sans font-semibold text-sm tracking-wide transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: loading ? '#c9a84c99' : 'linear-gradient(135deg, #c9a84c, #b8922e)', color: '#1a2b22' }}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="font-sans text-sm text-muted-foreground">
              New to REUSA?{' '}
              <Link href="/register" className="font-semibold text-primary hover:underline">
                Create an account
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link href="/" className="font-sans text-xs text-muted-foreground hover:text-primary transition-colors tracking-wide">
              ← Return to main site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
